import { motion } from "framer-motion";
import { Calendar, Award, Globe, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import CalendlySection from "@/components/landing/CalendlySection";
import WhatsAppButton from "@/components/landing/WhatsAppButton";
import ritaPhoto from "@/assets/rita-galmarini.png";
import juanPhoto from "@/assets/juan-pablo-barrios.png";

const stats = [
  { icon: Award, value: "+13", label: "años de trayectoria" },
  { icon: Building2, value: "+1.000", label: "marcas acompañadas" },
  { icon: Globe, value: "+15", label: "países" },
];

const team = [
  {
    name: "Rita Galmarini",
    role: "CEO & Founder · Estrategia de Marca y Negocios",
    photo: ritaPhoto,
    bio: `Rita Galmarini es estratega de marca y negocios, speaker y fundadora de CORESSEN.

Desde hace más de 13 años trabaja junto a empresarios y organizaciones en procesos de posicionamiento, transformación y crecimiento de marca.

Su trabajo parte de una premisa: una empresa puede haber construido muchísimo valor y, aun así, no estar ocupando la posición que le corresponde.

Desde CORESSEN dirige la estrategia para transformar trayectoria, reputación y conocimiento empresarial en Capital de Marca capaz de impulsar el negocio.`,
  },
  {
    name: "Juan Pablo Barrios",
    role: "Co-Founder · Dirección Creativa",
    photo: juanPhoto,
    bio: `Juan Pablo Barrios es cofundador y Director Creativo de CORESSEN.

Lidera la construcción conceptual y creativa de las marcas, convirtiendo la estrategia en sistemas capaces de expresar con claridad la posición y el nivel de cada compañía.

Su mirada combina dirección creativa, identidad y criterio estratégico para resolver uno de los desafíos centrales de CORESSEN: hacer visible el valor que muchas empresas ya construyeron puertas adentro.

Junto a Rita, dirige la evolución estratégica y creativa de los proyectos de CORESSEN.`,
  },
];

const QuienesSomosPage = () => {
  const scrollToCalendly = () => {
    const el = document.getElementById("calendly");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 bg-[#470628] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#470628] via-[#600832] to-[#470628]" />
        <div className="container px-4 relative z-10 text-center max-w-4xl mx-auto">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-block text-[#FF9FBD] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-6"
          >
            CORESSEN®
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
          >
            Quiénes <span className="text-[#FF9FBD]">somos</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto"
          >
            Consultora estratégica especializada en Capital de Marca para empresas y empresarios con trayectoria.
          </motion.p>
        </div>
      </section>

      {/* What we do */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container px-4 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="font-display text-xl md:text-2xl lg:text-3xl font-bold text-foreground leading-snug mb-6">
              Trabajamos sobre la brecha entre el valor que una empresa construyó y el valor que el mercado logra percibir.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Integramos estrategia de negocio, posicionamiento, percepción y comunicación para ayudar a nuestros clientes a ocupar una posición más sólida, diferenciarse y convertir su marca en un activo que impulse el negocio.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-gradient-to-b from-[#470628] to-[#600832] rounded-2xl p-8 md:p-10 text-white text-center shadow-xl"
          >
            <p className="font-display text-2xl md:text-3xl font-bold text-white">
              Elevamos la percepción a la altura del valor que construiste.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container px-4 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="text-center p-6 md:p-8 rounded-2xl bg-background border border-border/50 shadow-sm"
              >
                <stat.icon className="w-8 h-8 text-primary mx-auto mb-4" />
                <p className="font-display text-4xl md:text-5xl font-bold text-foreground mb-2">
                  {stat.value}
                </p>
                <p className="text-muted-foreground text-sm md:text-base">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trajectory */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container px-4 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
              Nuestra trayectoria
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-6 text-muted-foreground text-base md:text-lg leading-relaxed"
          >
            <p>
              Durante más de una década trabajamos junto a empresarios, líderes y organizaciones de distintas industrias y mercados.
            </p>
            <p>
              Esa experiencia nos permitió identificar algo que se repetía especialmente en empresas con trayectoria: muchas habían construido mucho más valor del que estaban logrando capitalizar.
            </p>
            <p>
              Buenos equipos. Grandes clientes. Experiencia. Reputación. Conocimiento. Años de mercado.
            </p>
            <p>
              Pero ese valor no siempre se traducía en una posición equivalente.
            </p>
            <p className="font-display text-lg md:text-xl font-semibold text-foreground">
              De ahí nace nuestra mirada sobre Capital de Marca: identificar, ordenar y elevar esos activos para que la percepción acompañe la verdadera dimensión de la empresa.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container px-4 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-4">
              El equipo
            </h2>
          </motion.div>

          <div className="space-y-16 md:space-y-24">
            {team.map((member, i) => (
              <motion.article
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6 }}
                className={`flex flex-col ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"} gap-8 md:gap-12 items-center`}
              >
                <div className="w-full md:w-2/5">
                  <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-xl bg-[#470628]">
                    <img
                      src={member.photo}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="w-full md:w-3/5">
                  <h3 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-2">
                    {member.name}
                  </h3>
                  <p className="text-primary font-semibold text-sm md:text-base uppercase tracking-wider mb-6">
                    {member.role}
                  </p>
                  <div className="text-muted-foreground leading-relaxed whitespace-pre-line space-y-4">
                    {member.bio.split("\n\n").map((paragraph, idx) => (
                      <p key={idx}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#470628] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#470628] to-[#600832]" />
        <div className="container px-4 relative z-10 text-center max-w-2xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6 leading-tight"
          >
            Conversemos
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white/80 text-base sm:text-lg leading-relaxed mb-10"
          >
            Si tu empresa construyó valor y querés que el mercado lo perciba a la altura que corresponde, agendemos una Llamada de Claridad.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Button
              variant="gold"
              size="xl"
              onClick={scrollToCalendly}
              className="text-base sm:text-lg px-8 sm:px-12"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Agendar Llamada de Claridad
            </Button>
          </motion.div>
        </div>
      </section>

      <CalendlySection />
      <Footer />
      <WhatsAppButton />
    </main>
  );
};

export default QuienesSomosPage;
